import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import AdminPracticeReportSummary from '../components/AdminPracticeReportSummary';
import {
  PRACTICE_LABELS,
  accuracyPercent,
  attemptTimestamp,
  buildPracticeAttemptPath,
  fetchPracticeAssignment,
  fetchPracticeAttemptHistory,
  normalizePracticeReport,
  practiceAssignmentFromPayload,
  startPracticeAgain,
} from '../lib/practiceAttempts';
import { formatIeltsBand, ieltsBandFromAttempt } from '../lib/ieltsSpeaking';

const PAGE_SIZE = 10;

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const errorText = (error, t) => {
  if (error?.status === 401 || error?.status === 403) return t('You do not have access to this practice.');
  if (error?.status === 404) return t('This practice result was not found.');
  return error?.message ? t(error.message) : t('The practice history could not be loaded.');
};

export default function PracticeAttemptHistoryPage({ viewer = 'student' }) {
  const { locale, t } = useI18n();
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [report, setReport] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1, has_next: false, has_previous: false });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!assignmentId) return;
    setLoading(true);
    setError('');
    try {
      const history = await fetchPracticeAttemptHistory({ assignmentId, viewer, page, pageSize: PAGE_SIZE });
      setAssignment(history.assignment || null);
      setAttempts(history.attempts || []);
      setReport(history.report ? normalizePracticeReport(history.report) : null);
      setPagination(history.pagination);
      if (!history.assignment) {
        try {
          const assignmentResponse = await fetchPracticeAssignment(assignmentId, viewer);
          setAssignment(practiceAssignmentFromPayload(assignmentResponse));
        } catch (_) {
          // The history response is still useful when the assignment envelope is unavailable.
        }
      }
    } catch (loadError) {
      setError(errorText(loadError, t));
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, [assignmentId, page, t, viewer]);

  useEffect(() => { load(); }, [load]);

  const practiceType = assignment?.practice_type || report?.assignment?.practice_type;
  const totalPages = Math.max(1, pagination.total_pages || 1);
  const canStartAgain = viewer === 'student' && Boolean(assignment?.id);
  const ieltsBands = practiceType === 'ielts_speaking'
    ? attempts.map((attempt) => ieltsBandFromAttempt(attempt)).filter((value) => value != null)
    : [];
  const ieltsChange = ieltsBands.length >= 2 ? Math.round((ieltsBands[0] - ieltsBands[ieltsBands.length - 1]) * 2) / 2 : null;

  const startAgain = async () => {
    if (!canStartAgain || starting) return;
    setStarting(true);
    setError('');
    try {
      navigate(await startPracticeAgain(assignment.id));
    } catch (startError) {
      setError(errorText(startError, t));
    } finally {
      setStarting(false);
    }
  };

  const heading = useMemo(() => (
    assignment?.title || report?.assignment?.title || t('Practice history')
  ), [assignment?.title, report?.assignment?.title, t]);

  if (loading) return <p className="p-8 text-center text-gray-600">{t('Loading attempt history...')}</p>;

  return (
    <main className="mx-auto max-w-5xl p-4 md:p-8">
      <header className="border-b border-gray-200 pb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <Link to={viewer === 'admin' ? '/admin/dashboard' : '/student/quizzes'} className="text-sm font-semibold text-indigo-700 hover:underline">{t('Back to practice')}</Link>
            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-indigo-700">{t(PRACTICE_LABELS[practiceType] || 'Practice')}</div>
            <h1 className="mt-1 text-2xl font-bold text-gray-950 sm:text-3xl" data-i18n-skip>{heading}</h1>
            <p className="mt-2 text-sm text-gray-600">{t('View every saved attempt for this assigned practice.')}</p>
            {ieltsChange != null && <p className={`mt-2 text-sm font-semibold ${ieltsChange >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{t('Predicted band')} {ieltsChange >= 0 ? '+' : '-'}{Math.abs(ieltsChange).toFixed(1).replace(/\.0$/, '')} {t('band change')}</p>}
          </div>
          {canStartAgain && (
            <button type="button" onClick={startAgain} disabled={starting} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-50">
              {starting ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" />}
              {t('Practice again')}
            </button>
          )}
        </div>
      </header>

      {error && <div className="mt-5 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</div>}
      {viewer === 'admin' && <AdminPracticeReportSummary report={report} t={t} />}

      <section className="mt-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('All attempts')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('{count} attempts', { count: pagination.total })}</p>
          </div>
          <button type="button" onClick={load} title={t('Refresh')} aria-label={t('Refresh')} className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-50">
            <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {attempts.length === 0 ? (
          <div className="mt-4 border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">{t('No attempts found for this practice.')}</div>
        ) : (
          <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
            {attempts.map((attempt) => {
              const percent = accuracyPercent(attempt.accuracy);
              const isIelts = practiceType === 'ielts_speaking';
              const predictedBand = isIelts ? ieltsBandFromAttempt(attempt) : null;
              return (
                <div key={attempt.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900">{t('Attempt {number}', { number: attempt.attempt_no || '?' })}</span>
                      {attempt.attempt_no === 1 && <span className="rounded bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">{t('First attempt')}</span>}
                      <span className={`text-sm font-semibold ${attempt.status === 'completed' ? 'text-emerald-700' : 'text-amber-700'}`}>{t(attempt.status === 'completed' ? 'Completed' : 'Submitted')}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span data-i18n-skip>{formatDate(attemptTimestamp(attempt), locale) || t('Not available')}</span>
                      {isIelts
                        ? predictedBand != null && <span>{t('Predicted band')}: <span data-i18n-skip>{formatIeltsBand(predictedBand)}</span></span>
                        : percent != null && <span>{t('Accuracy')}: {percent}%</span>}
                    </div>
                  </div>
                  <Link to={buildPracticeAttemptPath(attempt.id, viewer)} className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline">
                    <EyeIcon className="h-4 w-4" aria-hidden="true" />
                    {t('View details')}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {attempts.length > 0 && totalPages > 1 && (
          <nav className="mt-5 flex items-center justify-between gap-3" aria-label={t('Attempt pagination')}>
            <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={!pagination.has_previous} className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40">
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
              {t('Previous page')}
            </button>
            <span className="text-sm text-gray-600">{t('Page {page} of {pages}', { page: pagination.page, pages: totalPages })}</span>
            <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={!pagination.has_next} className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40">
              {t('Next page')}
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </nav>
        )}
      </section>
    </main>
  );
}
